import { useCallback, useMemo } from 'react';

import { useActorRef, useSelector } from '@xstate/react';

import { type IdMeAuthorizationResult } from '../../idme.types';
import { idMeVerificationMachine } from '../../machine';
import { useFetchVerifiedGroupsActor, useVerifyGroupActor } from '../../machine/actors';
import { useIdMeVerificationTracking } from '../useIdMeVerificationTracking';

import type { VerificationResult } from '../../machine/setup';
import type * as Actions from '../../machine/setup/actions';

// ─────────────────────────────────────────────────────────────────────────────

export const useIdMeVerification = (params: Params) => {
  const { entrySource, onCancelled, onFailed, onNotVerified, onLinkConflict } = params;

  const fetchVerifiedGroups = useFetchVerifiedGroupsActor();
  const verifyGroup = useVerifyGroupActor();
  const tracking = useIdMeVerificationTracking({ entrySource });

  // ─── Machine ───────────────────────────────────────────────────────────────

  const showLinkConflictNotice = useCallback(
    (_args: unknown, params: LinkConflictNoticeParams) => {
      onLinkConflict(params.verificationResult);
    },
    [onLinkConflict],
  );

  const providedMachine = useMemo(() => {
    return idMeVerificationMachine.provide({
      actions: {
        showCancelledNotice: onCancelled,
        showFailedNotice: onFailed,
        showNotVerifiedNotice: onNotVerified,
        showLinkConflictNotice,
        ...tracking,
      },
      actors: { fetchVerifiedGroups, verifyGroup },
    });
  }, [
    onCancelled,
    onFailed,
    onNotVerified,
    showLinkConflictNotice,
    tracking,
    fetchVerifiedGroups,
    verifyGroup,
  ]);

  const actorRef = useActorRef(providedMachine);

  // ─── State ─────────────────────────────────────────────────────────────────

  const isVerified = useSelector(actorRef, (snapshot) => snapshot.matches('verified'));
  const isLoading = useSelector(actorRef, (snapshot) => snapshot.matches({ setup: 'loading-verified-groups' })); // prettier-ignore
  const hasLoadFailed = useSelector(actorRef, (snapshot) => snapshot.matches({ setup: 'failed' }));
  const isVerifying = useSelector(actorRef, (snapshot) => snapshot.matches({ verification: 'creating-request' }) || snapshot.matches({ verification: 'verifying' })); // prettier-ignore
  const isPreparingRequest = useSelector(actorRef, (snapshot) => snapshot.children.prepareRequest?.getSnapshot().status === 'active'); // prettier-ignore
  const isAuthorizing = useSelector(actorRef, (snapshot) => snapshot.matches({ verification: 'authorizing' })); // prettier-ignore
  const isSigningOut = useSelector(actorRef, (snapshot) => snapshot.matches('signing-out'));
  const authorizationRequest = useSelector(actorRef, (snapshot) => (snapshot.matches({ verification: 'authorizing' }) ? snapshot.context.request : undefined)); // prettier-ignore
  const verifiedGroups = useSelector(actorRef, (snapshot) => snapshot.context.verifiedGroups);
  const canSignOut = useSelector(actorRef, (snapshot) => snapshot.can({ type: 'SIGN_OUT' }));

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const retry = useCallback(() => {
    actorRef.send({ type: 'RETRY' });
  }, [actorRef]);

  const verify = useCallback(() => {
    actorRef.send({ type: 'VERIFY' });
  }, [actorRef]);

  const signOut = useCallback(() => {
    actorRef.send({ type: 'SIGN_OUT' });
  }, [actorRef]);

  const reportAuthorizationResult = useCallback(
    (result: IdMeAuthorizationResult) => {
      actorRef.send({ type: 'AUTHORIZATION_RESULT', result });
    },
    [actorRef],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isVerified,
    isLoading,
    hasLoadFailed,
    isVerifying,
    isPreparingRequest,
    isAuthorizing,
    isSigningOut,
    authorizationRequest,
    verifiedGroups,
    canSignOut,
    retry,
    verify,
    signOut,
    reportAuthorizationResult,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  entrySource: Parameters<typeof useIdMeVerificationTracking>[0]['entrySource'];
  onCancelled: () => void;
  onFailed: () => void;
  onNotVerified: () => void;
  onLinkConflict: (verificationResult: VerificationResult) => void;
};

type LinkConflictNoticeParams = Parameters<typeof Actions.showLinkConflictNotice>[1];
